/*
 * cart-promo-gift-310 — auto-adds a free gift when the cart holds N+ qualifying variants.
 * Promo: buy 2x 28-serving shakes -> 1 free 310 Hydrate Variety Box (capped at 1).
 *
 * Pairs with a native automatic BXGY discount that zeroes the gift line ($0 in cart + checkout).
 * This component ONLY adds/removes/caps the gift line; it never sets price.
 *
 * Isolated from cart-free-gifts-310.js (the $-threshold ladder) — different line property,
 * and the gift product carries no `custom.is_cart_free_gift` metafield, so the ladder ignores it.
 *
 * "First order only, never recurring": the gift is added as a ONE-TIME line (no selling plan),
 * so it rides only on the initial checkout — Stay AI recurring orders never include it.
 *
 * TRIGGER MODES (cfg.mode):
 *   "quantity"     (default, original behaviour) — qualify on weighted bag count >= min_bags.
 *   "subscription" — qualify when the cart holds a line WITH a selling plan whose product_id is in
 *                    cfg.qualifyingProductIds. Used by the GLP-1 welcome kit, paired with automatic
 *                    BXGY 1187900293238 (customerBuys.isSubscription = true, usesPerOrderLimit = 1).
 *                    That BXGY is what actually zeroes the kit; this component only adds/removes the
 *                    line, so the gift product carries a REAL price ($24.99) and any bypass is charged.
 *                    Omit or misspell `mode` and the component behaves exactly as it always has.
 *
 * Free-box safeguard (≤1 free Hydrate box): after managing the gift line, run() also caps the
 * cart to AT MOST ONE $0 Hydrate box, no matter how it was zeroed — the BOGO gift line OR the
 * FREEHYDRATE 100%-off code applied to manually-added boxes. A box unit is "free" ONLY when the
 * cart discounted it to $0 (final_price === 0); paid or partially-discounted boxes (e.g. a
 * 40%-off upsell at $26.99, or a full-price box) are NEVER touched, and we never drop the last
 * remaining free box. The excess-removal folds into the SAME /cart/update.js batch as the gift
 * logic so the whole run stays idempotent (no loop) and never fights the auto-add.
 */
if (!customElements.get("cart-promo-gift-310")) {
  customElements.define(
    "cart-promo-gift-310",
    class CartPromoGift310 extends HTMLElement {
      connectedCallback() {
        this.init();
      }

      async init() {
        const node = document.querySelector("[data-promo-gift-settings]");
        try {
          this.cfg = JSON.parse(node?.textContent || "null");
        } catch (e) {
          this.cfg = null;
        }
        if (!this.cfg || !this.cfg.enabled) return;

        // Trigger mode. "quantity" (default, unchanged) qualifies on weighted bag count.
        // "subscription" qualifies on the presence of a SUBSCRIPTION line for a listed product.
        this.mode = this.cfg.mode === "subscription" ? "subscription" : "quantity";
        this.giftId = Number(this.cfg.gift_variant_id || this.cfg.giftVariantId);
        // Subscription mode: product_ids whose subscription lines earn the gift.
        this.subProductIds = new Set((this.cfg.qualifyingProductIds || []).map(Number));
        // Subscription mode may hand out MORE than one gift (kit + free Hydrate). Each has its
        // own capped BXGY; this only adds/removes the lines. Quantity mode stays single-gift.
        this.giftIds = [this.giftId];
        if (this.cfg.mode === "subscription") {
          (this.cfg.extraGiftVariantIds || []).forEach((id) => {
            const n = Number(id);
            if (n && this.giftIds.indexOf(n) === -1) this.giftIds.push(n);
          });
        }
        // Weighted bag map: variant_id -> bags (single=1, 2-Bags=2, 3-Bags=3).
        this.bags = new Map();
        const bagMap = this.cfg.qualifying_bags || {};
        Object.keys(bagMap).forEach((k) => this.bags.set(Number(k), Number(bagMap[k])));
        // Back-compat: fold any legacy flat qualifying_variant_ids in at weight 1.
        (this.cfg.qualifying_variant_ids || []).forEach((id) => {
          const n = Number(id);
          if (!this.bags.has(n)) this.bags.set(n, 1);
        });
        this.minBags = this.cfg.min_bags || this.cfg.min_qty || 2;
        this.prop = this.cfg.property || "_promo_free_gift";
        if (!this.giftId) return;
        if (this.mode === "subscription") {
          if (this.subProductIds.size === 0) return;
        } else if (this.bags.size === 0) return;

        this.busy = false;
        this.pending = false;
        this._failed = this._failed || new Set();

        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          subscribe(PUB_SUB_EVENTS.cartUpdate, (e) => this.run(e));
        }
        // The campaign LP buy box does NOT go through the theme's pub/sub. lp-tier-selector-core-310
        // posts /cart/add.js itself and then, when the theme has a <cart-drawer> with renderContents
        // (ours does), calls renderContents directly and dispatches NOTHING — the
        // 'cart-drawer:refresh' event in its else-branch is only a fallback for drawer-less themes.
        // So subscribe() above never fires on an LP add-to-cart: the shopper clicks ADD TO CART, the
        // drawer opens, and the promised gifts are simply absent until the next page load.
        // Listen for the fallback event AND watch the drawer for DOM changes, which is the same
        // approach pm-kit-guard-310 uses for exactly this reason. Debounced, and run() early-returns
        // when the cart is already in the desired state, so the refresh we trigger cannot loop.
        document.addEventListener("cart-drawer:refresh", () => this.schedule());
        var drawer = document.querySelector("cart-drawer");
        if (drawer && "MutationObserver" in window) {
          new MutationObserver(() => this.schedule()).observe(drawer, { childList: true, subtree: true });
        }
        // Initial evaluation (page load / cart already populated).
        await this.run({ source: "product-form" });
      }

      schedule() {
        clearTimeout(this._t);
        this._t = setTimeout(() => this.run({ source: "product-form" }), 350);
      }

      async getCart() {
        try {
          const res = await fetch("/cart.js");
          return await res.json();
        } catch (e) {
          console.error("[promo-gift] cart fetch failed", e);
          return null;
        }
      }

      // Add ONE gift, tolerating failure. /cart/add.js is ALL-OR-NOTHING when given an items[]
      // array: if any single line is rejected (sold out, unavailable) Shopify 422s the whole
      // request and adds NOTHING. Batching the gifts together therefore means a sold-out Hydrate
      // claim variant silently takes the shaker down with it. Add them one at a time instead so a
      // gift that cannot be fulfilled costs only itself.
      async addGift(item) {
        try {
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [item] }),
          });
          if (!res.ok) {
            this._failed.add(item.id);
            console.warn("[promo-gift] gift unavailable, skipped", item.id, res.status);
            return false;
          }
          return true;
        } catch (e) {
          this._failed.add(item.id);
          console.warn("[promo-gift] gift add failed", item.id, e);
          return false;
        }
      }

      async post(url, body) {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        return res.json();
      }

      lockCheckout(disabled) {
        const btns = document.querySelectorAll(
          'cart-drawer button[name="checkout"], form[action$="/cart"] button[name="checkout"]'
        );
        btns.forEach((b) => {
          if (disabled) {
            b.setAttribute("disabled", "true");
            b.dataset.promoLabel = b.dataset.promoLabel || b.textContent;
            b.textContent = "Updating your free gift…";
          } else if (b.dataset.promoLabel) {
            b.removeAttribute("disabled");
            b.textContent = b.dataset.promoLabel;
            delete b.dataset.promoLabel;
          }
        });
      }

      async refresh() {
        const drawer = document.querySelector("cart-drawer");
        if (drawer && typeof drawer.refreshCartDrawer === "function") {
          await drawer.refreshCartDrawer();
        }
      }

      async run(e) {
        // Coalesce overlapping events so we always settle on the final cart state.
        if (this.busy) {
          this.pending = true;
          return;
        }

        let cart = e && e.cartData;
        if (!cart || (e && e.source === "product-form")) cart = await this.getCart();
        if (!cart || !Array.isArray(cart.items)) return;
        // A later manual add clears the page's failed attempt for that variant.
        cart.items.forEach((i) => this._failed.delete(Number(i.variant_id)));

        // Count one-time AND subscription qualifying BAGS (weighted: single=1, 2-Bags=2, 3-Bags=3),
        // so a single "2 Bags"/"3 Bags" line counts as 2/3 toward the threshold. REQUIRES the paired
        // BXGY discounts' "Customer buys" Purchase type = "Both" (one-time + subscription) so the gift
        // is zeroed on subscription carts too — otherwise a subscription-triggered gift is ADDED BUT CHARGED.
        let qualifies;
        if (this.mode === "subscription") {
          // Subscription mode: qualify ONLY on a line that carries a selling plan AND belongs to a
          // listed product. The paired BXGY (customerBuys.isSubscription = true) is what zeroes the
          // gift; this component only adds/removes the line, so a bypass still gets charged.
          qualifies = cart.items.some(
            (i) => i.selling_plan_allocation && this.subProductIds.has(Number(i.product_id))
          );
        } else {
          const qualifyingBags = cart.items.reduce(
            (sum, i) => sum + (this.bags.get(i.variant_id) || 0) * i.quantity,
            0
          );
          qualifies = qualifyingBags >= this.minBags;
        }

        // One free kit per cart: an Island Kit already in the cart blocks (and clears) the
        // welcome kit, so one Tropical Coconut subscription can never collect both kits.
        if (this.mode === "subscription" && cart.items.some((i) => i.properties && i.properties._island_kit_gift)) {
          qualifies = false;
        }

        const giftLine = cart.items.find(
          (i) => i.variant_id === this.giftId && i.properties && i.properties[this.prop]
        );

        const adds = [];
        const updates = {};

        // (1) Gift auto-add / cap / remove, keyed by the line property. Runs once per gift
        // variant so subscription mode can carry the kit AND the free Hydrate; in quantity
        // mode giftIds holds exactly one id, so behaviour is unchanged.
        this.giftIds.forEach((gid) => {
          const line = cart.items.find(
            (i) => i.variant_id === gid && i.properties && i.properties[this.prop]
          );
          if (qualifies && !line) {
            if (!this._failed.has(gid)) adds.push({ id: gid, quantity: 1, properties: { [this.prop]: "true" } });
          } else if (qualifies && line && line.quantity !== 1) {
            updates[line.key] = 1; // enforce cap of 1
          } else if (!qualifies && line) {
            updates[line.key] = 0; // remove when no longer qualifying
          }
        });

        // (2) Free-box safeguard — QUANTITY MODE ONLY.
        //
        // Skipped entirely in subscription mode. This block was written for the BOGO promo, where
        // giftId was the free Hydrate BOX and the job was to cap "$0 boxes" however they got zeroed.
        // In subscription mode giftId is the $0 shaker kit, which is ALWAYS final_price === 0, so the
        // block misreads it as a duplicate free box: if any gift still needs adding (e.g. the Hydrate
        // claim variant is sold out and its add keeps failing) then addingGift is true, totalFree
        // computes 1 + 1 = 2, and the "keep only the incoming gift" branch zeroes the shaker the
        // shopper just earned. Step (1) already caps every gift at qty 1 per variant, so subscription
        // mode does not need this at all.
        if (this.mode !== "subscription") {

          const addingGift = adds.length > 0;
          // Project each free box line's post-(1) quantity: honor the gift-line cap/remove already
          // queued by (1) and drop lines (1) removes, so we neither double-count nor over-cap.
          const freeCandidates = cart.items
            .filter((i) => i.variant_id === this.giftId && i.final_price === 0)
            .map((i) => ({
              key: i.key,
              qty:
                giftLine && i.key === giftLine.key && updates[i.key] !== undefined
                  ? updates[i.key]
                  : i.quantity,
              isGift: !!(i.properties && i.properties[this.prop]),
            }))
            .filter((c) => c.qty > 0);

          // Free units that would remain after (1): surviving free lines + the pending gift add.
          const totalFree = freeCandidates.reduce((s, c) => s + c.qty, 0) + (addingGift ? 1 : 0);
          if (totalFree > 1) {
            if (addingGift) {
              // The incoming gift is the single kept free box — zero every existing free box line.
              freeCandidates.forEach((c) => {
                if (c.qty !== 0) updates[c.key] = 0;
              });
            } else {
              // Keep exactly ONE survivor at qty 1 (prefer the tagged gift line); zero the rest.
              const survivor = freeCandidates.find((c) => c.isGift) || freeCandidates[0];
              freeCandidates.forEach((c) => {
                const target = c.key === survivor.key ? 1 : 0;
                if (c.qty !== target) updates[c.key] = target; // only write real changes -> idempotent
              });
            }
          }
        }

        // Already in the desired state (nothing to add, nothing to change) — prevents loops.
        if (!adds.length && !Object.keys(updates).length) return;

        this.busy = true;
        this.lockCheckout(true);
        try {
          for (const item of adds) await this.addGift(item);
          if (Object.keys(updates).length) await this.post("/cart/update.js", { updates });
          await this.refresh();
        } catch (err) {
          console.error("[promo-gift] update failed", err);
        } finally {
          this.busy = false;
          this.lockCheckout(false);
          if (this.pending) {
            this.pending = false;
            this.run({ source: "product-form" });
          }
        }
      }
    }
  );
}
